import type { Components, Theme } from '@mui/material/styles';
import { radius } from '@/tokens/radius';
import { chipHeights, controlHeights } from '@/tokens/sizes';
import { shadows } from '@/tokens/shadows';
import { spacing } from '@/tokens/spacing';
import { compactGlyphScale, fontSize, lineHeight } from '@/tokens/typography';
import {
  SURFACE_LADDER,
  backgroundColors,
  borderByRung,
  inputColors,
  lightBackgroundColors,
  lightInputColors,
  lightTextColors,
  scrim,
  separatorByRung,
  tableHeaderByRung,
  textColors,
} from '@/tokens/colors';
import { dsVar, scopeToCssVars, surfaceScopes } from '@/tokens/surfaceScopes';
import {
  buttonVariants,
  buttonSizeVariants,
  chipSizeVariants,
  subtleChipVariants,
  neutralSubtleChipVariants,
  fieldChipVariants,
  dotChipVariants,
  filledChipColorVariants,
  outlinedChipColorVariants,
  neutralOutlinedChipVariants,
  lifecycleChipVariants,
} from './variants';

/**
 * The paper of a portalled floating element — a menu, a popover, a picker.
 *
 * These all obey `max(raised, host + 1)`, and this covers the `raised` floor of
 * that rule. It cannot cover the `host + 1` half, because a portalled element is
 * reparented to `document.body`: it has no DOM ancestor to inherit `--ds-raised`
 * from, and `styleOverrides` cannot read React context to find out what opened
 * it. So a menu opened from a dialog currently sits at `raised` rather than
 * climbing above the dialog's `surface`. `raised` is one rung above `surface`,
 * so the common case still separates correctly; the case this misses is a menu
 * inside something already at `raised` or higher. `usePortalRung` is the hook
 * that closes it, and it needs component wrappers rather than theme overrides.
 *
 * Light mode has no separate overlay rung, so paper (white) is the correct lift
 * off the page there and the ladder collapses to two surfaces by design.
 */
function floatingPaper(corner: 'md' | 'lg') {
  return ({ theme }: { theme: Theme }) => ({
    borderRadius: radius[corner],
    border: `1px solid ${theme.palette.mode === 'dark' ? borderByRung.raised : theme.palette.divider}`,
    backgroundColor:
      theme.palette.mode === 'dark' ? backgroundColors.raised : theme.palette.background.paper,
    backgroundImage: 'none',
    boxShadow: shadows.md,
    ...scopeToCssVars(surfaceScopes[theme.palette.mode].raised),
  });
}

/**
 * MUI component overrides + defaultProps.
 *
 * This is where the design system applies its opinions on top of MUI without
 * forking component implementations - the core of "theme layer over MUI".
 */
export const components: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: (theme) => {
      const dark = theme.palette.mode === 'dark';
      const scopes = surfaceScopes[theme.palette.mode];
      const scrollThumb = dark ? backgroundColors.border : lightBackgroundColors.border;
      return {
        // Declare the mode to the browser so *native* widget chrome follows the
        // theme: date/time picker indicators, native <select> popups and
        // scrollbars otherwise render their light-mode (black-on-dark) glyphs
        // regardless of what the surrounding UI looks like.
        html: { colorScheme: theme.palette.mode },
        body: {
          scrollbarColor: `${scrollThumb} transparent`,
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: scrollThumb,
            borderRadius: radius.pill,
          },
          // Unscoped fields/buttons inherit the `primary` scope — the same rung
          // as `palette.background.default` — so a bare TextField in Storybook
          // or a page root recesses onto `page` instead of falling back to the
          // dark `inputColors.background` hex.
          ...scopeToCssVars(scopes.primary),
        },
        // `data-surface` opens a scope on any element, not just `Surface`.
        //
        // Declaring the six scopes once here rather than inlining ten custom
        // properties per instance is what lets a *themed MUI* component join the
        // ladder: a Drawer paper or a Dialog paper can set one attribute through
        // `defaultProps` and pick up the whole scope, which it could not do from
        // `styleOverrides` alone. It also means a plain `<div data-surface="widget">`
        // works in a story or a consumer's app with no import.
        //
        // Both these rules and `Surface`'s inline properties are generated from
        // `surfaceScopes`, so the two cannot disagree.
        ...Object.fromEntries(
          SURFACE_LADDER.map((rung) => [`[data-surface="${rung}"]`, scopeToCssVars(scopes[rung])]),
        ),
      };
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
      variant: 'secondary',
    },
    styleOverrides: {
      root: {
        borderRadius: radius.sm,
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    variants: [...buttonVariants, ...buttonSizeVariants],
  },
  MuiIconButton: {
    styleOverrides: {
      root: { borderRadius: radius.sm },
    },
  },
  MuiPaper: {
    styleOverrides: {
      /**
       * `elevation` selects a rung, not a shadow.
       *
       * On a dark, dense UI a drop shadow has almost nothing to fall on — the
       * separation has to come from tone. So MUI's 25-step elevation scale is
       * collapsed onto the ladder (Component System §08). The practical effect
       * on consumers: passing `elevation` now changes the background rather
       * than the shadow.
       *
       * Values are read as `var(--ds-*)` so the same `elevation` lands on a
       * different absolute colour depending on where the Paper is mounted,
       * which is the point of the ladder. The fallbacks cover a Paper rendered
       * outside any surface scope.
       */
      root: ({ ownerState }) => {
        const elevation = Number(ownerState.elevation ?? 1);
        const background =
          elevation === 0
            ? // Not a surface of its own: sits flush on whatever hosts it.
              dsVar('bg', backgroundColors.widget)
            : elevation <= 3
              ? // A contained surface: one rung above its host.
                dsVar('child', backgroundColors.widget)
              : elevation <= 8
                ? // Floating: the portal rung, so it clears its host.
                  dsVar('raised', backgroundColors.raised)
                : // The ceiling.
                  backgroundColors.overlay;
        return { backgroundImage: 'none', backgroundColor: background };
      },
      rounded: { borderRadius: radius.md },
    },
  },
  MuiCard: {
    // Elevation 1 rather than 0: a card *is* a contained surface, so it should
    // step one rung above its host. At 0 it would paint flush and disappear.
    defaultProps: { elevation: 1 },
    styleOverrides: {
      root: {
        borderRadius: radius.md,
        // No border. The rung step above the host is what separates a card now;
        // keeping the 1px edge as well reads as a double boundary (§01 rule 1).
        // A card that genuinely needs an edge — selected, dragged — should set
        // one explicitly from `outlineByRung`.
        border: 'none',
        // Background comes from MuiPaper's elevation mapping above.
      },
    },
  },
  MuiAccordion: {
    // Same reasoning as Card: the accordion is the surface, and MUI's default
    // dividers plus its own border would triple up with the rung step.
    defaultProps: { elevation: 1, disableGutters: true },
    styleOverrides: {
      root: {
        borderRadius: radius.md,
        border: 'none',
        '&::before': { display: 'none' },
        '&:first-of-type, &:last-of-type': { borderRadius: radius.md },
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        // Spec §08: an avatar is a control-weight fill at host +2 carrying
        // `text.primary`, not a palette colour.
        //
        // Untouched, MUI resolves a dark avatar to `palette.grey[600]` for the
        // fill and `palette.background.default` for the initials. Both were
        // wrong here, and the second was the more serious: `#1F1F1F` ink on a
        // `#2B2B33` fill is 1.15:1, so the initials were effectively invisible.
        // The fill was also the one blue-tinted entry in the neutral scale.
        backgroundColor: dsVar('control', backgroundColors.surface),
        color: dsVar('text', textColors.primary),
        '& .MuiSvgIcon-root': { color: 'inherit' },
        // A photo avatar paints its own pixels, so the fill above never shows.
        // The inset hairline keeps a light-edged photo from bleeding into the
        // surface, which is the one thing a tonal step cannot do for it.
        '&:has(> img)': {
          boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.10)',
        },
      },
    },
  },
  MuiAvatarGroup: {
    styleOverrides: {
      // The overlap gap reads as a gap only if it matches what is behind it, so
      // it takes the host rung rather than a fixed colour.
      avatar: {
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: dsVar('bg', backgroundColors.primary),
      },
    },
  },
  MuiBadge: {
    styleOverrides: {
      // Spec §08: a badge is portable — a fixed severity fill with fixed ink,
      // identical on all six rungs — with one exception. The halo separating it
      // from whatever it is pinned to has to match the host, because its whole
      // job is to read as a gap. That makes it the one badge property that
      // scopes.
      badge: {
        boxShadow: `0 0 0 2px ${dsVar('bg', backgroundColors.primary)}`,
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      // Repointed off `palette.divider`, which is a single rung-agnostic value
      // and therefore wrong on most of the ladder: the flat `border` token is
      // 1.13:1 on `widget` and near-invisible on `surface`. `--ds-border`
      // resolves to the host rung's entry in `borderByRung`, holding ~1.35:1
      // everywhere — the weight MUI's own dark divider ships at.
      root: {
        borderColor: dsVar('border', borderByRung.widget),
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: radius.pill,
        fontWeight: 500,
        letterSpacing: '0.02em',
        fontSize: fontSize.sm,
        height: chipHeights.medium,
        // Deliberately no textTransform. Chips carry values as often as words --
        // hostnames, usernames, filenames, tenant slugs -- and capitalising
        // those corrupts them: `svc-backup` became "Svc-Backup". Callers pass
        // the casing they want, and `purpose="severity"` derives its own word.
      },
      sizeSmall: {
        height: chipHeights.small,
        fontSize: fontSize.xs,
        // The delete target, narrowed to what a 22px pill can hold. This lives
        // here rather than on the `deleteIcon` slot below for two reasons: this
        // MUI major exposes no `deleteIconSmall` slot, and a rule written from
        // the root outranks the single-class slot rule it has to override.
        // Small chips carry narrower margins than medium ones (-4/4), so the
        // padding is taken back out of those numbers instead.
        '& .MuiChip-deleteIcon': {
          padding: 3,
          marginLeft: -7,
          marginRight: 1,
        },
      },
      /**
       * MUI sizes the leading icon only for `size="small"`; the medium case is
       * left unstyled and falls back to the `SvgIcon` default of 24px. That is
       * survivable at MUI's own 32px chip, but this theme runs a 28px pill with
       * a 12px label, so the icon arrived at twice the text size and 86% of the
       * pill height. The delete glyph was MUI's 22px. Neither was related to
       * the label, so a single medium chip rendered four sizes: 12, 18, 22, 24.
       *
       * `em` ties every glyph to the label beside it. The small chip then
       * scales for free (11px -> ~14.7px), and the ratio survives any later
       * change to either font size instead of silently drifting.
       */
      icon: { fontSize: compactGlyphScale },
      /**
       * The glyph shrinks from 22px; the pointer target must not shrink with
       * it. MUI's delete icon is not separately focusable -- keyboard removal
       * is Backspace on the focused chip -- so clicking it is the only pointer
       * route to removing a chip.
       *
       * Padding grows the box to 24px (WCAG 2.5.8, and up from the 22px this
       * had), then the padding is taken back out of MUI's own asymmetric
       * margins so the glyph lands exactly where it did before. A centred
       * `::after` would read more cleanly but does nothing here: generated
       * content is not rendered on SVG elements, and `getComputedStyle` will
       * cheerfully report the box it never painted.
       */
      deleteIcon: {
        fontSize: compactGlyphScale,
        boxSizing: 'content-box',
        padding: 4,
        marginLeft: -10, // MUI's -6, less the 4px of padding added above
        marginRight: 1, // MUI's 5, less the same
      },
    },
    variants: [
      ...chipSizeVariants,
      ...subtleChipVariants,
      // Must follow the generic subtle variants: both match subtle+neutral
      // and the later variant wins.
      ...neutralSubtleChipVariants,
      ...fieldChipVariants,
      ...dotChipVariants,
      ...filledChipColorVariants,
      ...outlinedChipColorVariants,
      // Must follow the generic outlined variants: both match outlined+neutral
      // and the later variant wins.
      ...neutralOutlinedChipVariants,
      ...lifecycleChipVariants,
    ],
  },
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
      // `medium` matches Button's default height via `controlHeights.medium`.
      // Use `size="small"` only in dense toolbars and tables.
      size: 'medium',
      fullWidth: true,
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      /**
       * A field recesses one rung *below* its host, which is what makes it read
       * as inset rather than as a differently-coloured box sitting on top.
       * `--ds-field` resolves that from the surface scope, so the same field is
       * `#1F1F1F` inside a card and `#262626` inside a dialog (dark), or
       * `#E9E9E9` / `#F1F1F1` / `#F8F8F8` on the light ladder.
       *
       * Fallbacks are mode-aware: the dark `inputColors.background` (`#191919`)
       * must never paint a light canvas, or labels and placeholders vanish on a
       * black bar.
       *
       * Height comes from `controlHeights`, the same ladder Button uses.
       */
      root: ({ theme, ownerState }) => {
        const height =
          ownerState.size === 'small' ? controlHeights.small : controlHeights.medium;
        const chrome = theme.palette.mode === 'dark' ? inputColors : lightInputColors;
        return {
          borderRadius: radius.sm,
          backgroundColor: dsVar('field', chrome.background),
          color: theme.palette.text.primary,
          // Fixed height (not only minHeight): the input uses content-box, so
          // padding + line-box can overshoot `minHeight` by a fraction of a
          // pixel and disagree with a Button on the same rung.
          ...(!ownerState.multiline && { height, minHeight: height }),
          // Beat MUI's hover (`text.primary`) and color-variant focus
          // (`primary.main` = teal). `:not(.Mui-error)` keeps validation red.
          '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
            borderColor: chrome.borderHover,
          },
          '@media (hover: none)': {
            '&:hover:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
              borderColor: dsVar('fieldBorder', chrome.border),
            },
          },
          /**
           * Dark: `focusRing` equals `border`, so focus is currently off.
           * Light: `lightInputColors.focusRing` is `#1A1A1A`, so the outline
           * darkens on focus per Option A Light §03.
           */
          '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
            borderColor: chrome.focusRing,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: chrome.errorBorder,
          },
        };
      },
      /**
       * Padding is deliberately lighter than MUI's 16.5/8.5 so the content fits
       * inside `controlHeights`. Size branches live here rather than on an
       * `inputSizeSmall` key: MUI 9's `inputOverridesResolver` only forwards
       * `styles.input`, so a separate small slot is a no-op.
       */
      input: ({ theme, ownerState }) => {
        const small = ownerState.size === 'small';
        const placeholder =
          theme.palette.mode === 'dark' ? textColors.muted : lightTextColors.muted;
        return {
          fontSize: small ? fontSize.sm : fontSize.md,
          lineHeight: lineHeight.normal,
          padding: small ? `${spacing.xxs}px 12px` : `${spacing.xs}px 14px`,
          boxSizing: 'border-box',
          ...(!ownerState.multiline && {
            height: small ? controlHeights.small : controlHeights.medium,
          }),
          '&::placeholder': {
            color: placeholder,
            opacity: 1,
          },
        };
      },
      notchedOutline: ({ theme }) => {
        const chrome = theme.palette.mode === 'dark' ? inputColors : lightInputColors;
        return {
          borderColor: dsVar('fieldBorder', chrome.border),
        };
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      /**
       * MUI's outlined label transforms are calibrated for 56px / 40px fields.
       * Ours are `controlHeights` 36 / 28, so the resting label has to be
       * recentred or it sits too low inside the box.
       */
      outlined: ({ ownerState }) => {
        const small = ownerState.size === 'small';
        return {
          // Horizontal inset matches the input padding (12 small / 14 medium).
          transform: small
            ? 'translate(12px, 6px) scale(1)'
            : 'translate(14px, 10px) scale(1)',
          '&.MuiInputLabel-shrink': {
            transform: small
              ? 'translate(12px, -9px) scale(0.75)'
              : 'translate(14px, -9px) scale(0.75)',
          },
        };
      },
      root: ({ theme }) => {
        const quiet =
          theme.palette.mode === 'dark' ? textColors.subtle : lightTextColors.subtle;
        return {
          color: quiet,
          // MUI paints focused labels with `primary.main` (teal). Keep the
          // resting quiet colour so an active field is not brand-tinted.
          '&.Mui-focused:not(.Mui-error)': {
            color: quiet,
          },
        };
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => {
        const quiet =
          theme.palette.mode === 'dark' ? textColors.subtle : lightTextColors.subtle;
        return {
          // MUI paints focused labels with `primary.main` (teal). Keep the
          // resting quiet colour so an active field is not brand-tinted.
          '&.Mui-focused:not(.Mui-error)': {
            color: quiet,
          },
        };
      },
    },
  },
  MuiTooltip: {
    defaultProps: { arrow: true },
    styleOverrides: {
      /**
       * `raised`, not black. A pure-black tooltip was off the ladder entirely,
       * and once `page` stopped being black it read as a hole punched through
       * the app rather than as something floating above it. `raised` is the rung
       * reserved for tooltips and snackbars, and its ink is `text.tooltip`,
       * which was a near-black that only ever made sense on a light tooltip.
       *
       * A tooltip is portalled to `document.body`, so it cannot inherit
       * `--ds-raised` from whatever triggered it. That makes `raised` the floor
       * of `max(raised, host + 1)` rather than the full rule — a tooltip fired
       * from inside a dialog does not yet climb above it. See the note in the
       * commit; that half needs the context path, not `styleOverrides`.
       */
      tooltip: ({ theme }) => {
        const dark = theme.palette.mode === 'dark';
        // Light tooltips invert (dark fill + light ink) — a white tooltip on a
        // white ladder has no presence at any shadow strength (Option A Light §02).
        const fill = dark ? backgroundColors.raised : lightTextColors.primary;
        const ink = dark ? textColors.tooltip : lightTextColors.tooltip;
        const edge = dark ? borderByRung.raised : '#2F2F2F';
        return {
          ...theme.typography.caption,
          backgroundColor: fill,
          color: ink,
          borderRadius: radius.sm,
          border: `1px solid ${edge}`,
          boxShadow: shadows.md,
          // The tooltip is a surface in its own right, so it declares a scope for
          // anything rendered inside it. Dark keeps the raised scope; light's
          // inverted fill is off-ladder by design.
          ...(dark ? scopeToCssVars(surfaceScopes.dark.raised) : null),
        };
      },
      arrow: ({ theme }) => ({
        color:
          theme.palette.mode === 'dark' ? backgroundColors.raised : lightTextColors.primary,
      }),
    },
  },
  MuiDialogTitle: {
    defaultProps: { variant: 'subtitle1', component: 'h2' },
  },
  MuiDialogContentText: {
    defaultProps: { variant: 'body1' },
  },
  MuiAlert: {
    styleOverrides: {
      message: ({ theme }) => theme.typography.body1,
    },
  },
  MuiAlertTitle: {
    defaultProps: { variant: 'subtitle2' },
  },
  MuiCardHeader: {
    defaultProps: {
      slotProps: {
        title: { variant: 'subtitle1' },
        subheader: { variant: 'body1' },
      },
    },
  },
  MuiBreadcrumbs: {
    styleOverrides: {
      li: ({ theme }) => theme.typography.body1,
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: ({ theme }) => theme.typography.caption,
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: radius.lg,
        border: `1px solid ${borderByRung.surface}`,
        backgroundColor: backgroundColors.surface,
        backgroundImage: 'none',
        boxShadow: shadows.lg,
        // A dialog is the canonical `surface` and hosts a whole layout, so it
        // must open a scope: fields inside it recess to `widget`, a nested table
        // takes dialog-relative bands, and a menu opened from it climbs above it.
        ...scopeToCssVars(surfaceScopes[theme.palette.mode].surface),
      }),
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      // MUI's bare default (8px all round) leaves the buttons hugging the
      // paper edge. Match the title/content gutter and give the row breathing
      // room at the bottom; `gap` owns the space between actions, so the
      // margin MUI would add per-child is neutralized (and `disableSpacing`
      // keeps meaning "no space between actions").
      root: ({ ownerState }) => ({
        padding: `${spacing.sm}px ${spacing.lg}px ${spacing.md}px`,
        gap: ownerState.disableSpacing ? 0 : spacing.sm,
        '& > :not(style) ~ :not(style)': { marginLeft: 0 },
      }),
    },
  },
  MuiDrawer: {
    // A drawer is dismissible and leaves most of the page visible, so it takes
    // the middle scrim weight rather than a dialog's. Set through `slotProps`
    // because the backdrop is a sibling of the paper, not reachable from the
    // drawer's own `styleOverrides`.
    defaultProps: {
      slotProps: {
        backdrop: { sx: { backgroundColor: `rgba(0, 0, 0, ${scrim.drawer})` } },
      },
    },
    styleOverrides: {
      // A drawer is portalled, so it cannot inherit a rung through the DOM and
      // has to declare one. Inlining the scope rather than setting
      // `data-surface` also opens it for the drawer's contents, which is what
      // lets a field inside a drawer recess to the correct rung.
      paper: ({ theme }) => ({
        ...scopeToCssVars(surfaceScopes[theme.palette.mode].surface),
        backgroundColor: 'var(--ds-bg)',
        borderColor: 'var(--ds-border)',
        backgroundImage: 'none',
      }),
    },
  },
  MuiAppBar: {
    defaultProps: { elevation: 0, color: 'default' },
    styleOverrides: {
      // The app bar is the shell, so it takes the bottom rung rather than
      // floating above the content. Opening the scope here is what makes a
      // field on the app bar hit the `page` exception: there is no rung below
      // it to recess into, so the field goes transparent and its outline
      // carries the affordance instead.
      root: ({ theme }) => ({
        ...scopeToCssVars(surfaceScopes[theme.palette.mode].page),
        backgroundColor: 'var(--ds-bg)',
        borderBottom: `1px solid var(--ds-border)`,
        backgroundImage: 'none',
        boxShadow: shadows.none,
      }),
    },
  },
  MuiTable: {
    styleOverrides: {
      root: {
        // The body is the host fill. At the host rung this is the same colour
        // as a dedicated row token would have been — but naming the host gives
        // hover and selection a defined base to sit on instead of each tinting
        // whatever happens to be behind the table.
        backgroundColor: dsVar('bg', backgroundColors.primary),
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        // Row rhythm at ~1.12:1 via `--ds-separator`. Heavier `--ds-border` is
        // reserved for container outlines. A filled header has no underline.
        borderBottom: `1px solid ${dsVar('separator', separatorByRung.primary)}`,
        // Roomy default: 16px vertical / 24px horizontal breathing room.
        padding: `${spacing.md}px ${spacing.lg}px`,
        fontSize: '0.875rem', // 14px — matches the `md` type token
        lineHeight: 1.45,
        color: theme.palette.text.primary,
      }),
      head: {
        color: dsVar('tableHeaderLabel', textColors.tertiary),
        fontWeight: 600,
        fontSize: '0.875rem', // 14px — matches the `md` type token
        letterSpacing: '0.02em',
        lineHeight: 1.5,
        padding: `${spacing.md}px ${spacing.lg}px`,
        verticalAlign: 'middle',
        /**
         * ~1.08:1 recessed fill keyed to the host, moving away from elevation
         * (lighter in dark). Fill or underline, not both — sticky DataTables
         * add the separator as a bottom rule; raw MUI tables stay fill-only.
         */
        backgroundColor: dsVar('tableHeader', tableHeaderByRung.primary),
        borderBottom: 'none',
        whiteSpace: 'nowrap',
      },
      // Compact/dense tables tighten the vertical rhythm without losing the
      // horizontal breathing room.
      sizeSmall: {
        padding: `${spacing.sm + spacing.xs}px ${spacing.lg}px`,
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        transition: 'background-color 120ms ease',
        // No trailing separator on the final row for a clean bottom edge.
        '&:last-of-type > .MuiTableCell-root': {
          borderBottom: 'none',
        },
        // A concrete band, not `action.hover`'s translucent white. An alpha
        // hover compounded into a colour nobody picked, and it got weaker the
        // higher the table sat.
        '&.MuiTableRow-hover:hover': {
          backgroundColor: dsVar('tableRowHover', backgroundColors.surface),
        },
      },
      head: {
        '&.MuiTableRow-hover:hover': {
          backgroundColor: 'transparent',
        },
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: floatingPaper('md'),
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: floatingPaper('md'),
    },
  },
  MuiSnackbarContent: {
    styleOverrides: {
      // A snackbar shares the tooltip's rung: it floats over everything,
      // including an open dialog.
      root: ({ theme }) => ({
        backgroundColor: backgroundColors.raised,
        color: textColors.primary,
        borderRadius: radius.md,
        border: `1px solid ${borderByRung.raised}`,
        boxShadow: shadows.lg,
        backgroundImage: 'none',
        ...scopeToCssVars(surfaceScopes[theme.palette.mode].raised),
      }),
    },
  },
  MuiBackdrop: {
    styleOverrides: {
      /**
       * One scrim weight for everything was the wrong call in both directions:
       * heavy enough for a dialog made a menu feel like a mode change, and light
       * enough for a menu let a dialog's own scrim fail to separate it.
       *
       * `dialog` is the default because `MuiBackdrop` cannot see what opened it.
       * The lighter weights are opted into by the components that know — a menu
       * or popover only needs a scrim at all to catch the outside click.
       *
       * Opacities over black rather than colours: the scrim always sits on
       * black, whatever rung it is covering.
       */
      root: {
        backgroundColor: `rgba(0, 0, 0, ${scrim.dialog})`,
      },
      invisible: {
        backgroundColor: 'transparent',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: { textTransform: 'none', fontWeight: 600, minHeight: 44 },
    },
  },
};
